/**
 * ArraySliderControl - Compact canvas line-graph editor for array endpoints.
 *
 * Adapted from ArrayVisualization. Click/drag to paint values, snaps to step.
 * Double-click switches to inline text entry (unconstrained by min/max).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrayControl } from './ArrayControl';

const CANVAS_HEIGHT = 40;
const PIXEL_RATIO = 1.5;
const DOT_RADIUS = 2.5 * PIXEL_RATIO;
const PADDING_X = 4 * PIXEL_RATIO;
const PADDING_Y = 6 * PIXEL_RATIO;

interface ArraySliderControlProps {
  value: any[];
  length: number;
  min: number;
  max: number;
  step?: number;
  color: string;
  trackColor?: string;
  onChange: (value: any[]) => void;
}

export function ArraySliderControl({ value, length, min, max, step = 1, color, trackColor = '#1a1a1a', onChange }: ArraySliderControlProps) {
  const [textMode, setTextMode] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const lastDragCoordsRef = useRef<[number, number] | null>(null);
  const dragPatternRef = useRef<number[] | null>(null);
  const patternRef = useRef<number[]>([]);
  const [containerWidth, setContainerWidth] = useState(200);

  // Normalize value array to match expected length
  const pattern = normalizeArray(value, length, min);
  patternRef.current = pattern;

  const canvasWidth = Math.round(containerWidth * PIXEL_RATIO);
  const canvasHeight = Math.round(CANVAS_HEIGHT * PIXEL_RATIO);

  const plotLeft = PADDING_X;
  const plotRight = canvasWidth - PADDING_X;
  const plotTop = PADDING_Y;
  const plotBottom = canvasHeight - PADDING_Y;
  const plotWidth = plotRight - plotLeft;
  const plotHeight = plotBottom - plotTop;

  // Measure container width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const indexToX = useCallback((i: number): number => {
    const len = patternRef.current.length;
    if (len <= 1) return plotLeft + plotWidth / 2;
    return plotLeft + (i / (len - 1)) * plotWidth;
  }, [plotLeft, plotWidth]);

  const valueToY = useCallback((v: number): number => {
    const range = max - min;
    if (range === 0) return plotTop + plotHeight / 2;
    return plotBottom - ((v - min) / range) * plotHeight;
  }, [min, max, plotTop, plotBottom, plotHeight]);

  const yToValue = useCallback((y: number): number => {
    const range = max - min;
    const raw = min + ((plotBottom - y) / plotHeight) * range;
    const snapped = Math.round(raw / step) * step;
    return Math.min(max, Math.max(min, +snapped.toFixed(10)));
  }, [min, max, step, plotBottom, plotHeight]);

  const xToIndex = useCallback((x: number): number | null => {
    const len = patternRef.current.length;
    if (len === 0) return null;
    if (len === 1) return 0;
    const t = (x - plotLeft) / plotWidth;
    const idx = Math.round(t * (len - 1));
    if (idx < 0 || idx >= len) return null;
    return idx;
  }, [plotLeft, plotWidth]);

  const getCanvasCoords = useCallback((e: MouseEvent | React.MouseEvent): [number, number] | null => {
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (!rect || !canvas) return null;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return [(e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY];
  }, []);

  // Stable refs for window handlers
  const xToIndexRef = useRef(xToIndex);
  xToIndexRef.current = xToIndex;
  const yToValueRef = useRef(yToValue);
  yToValueRef.current = yToValue;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const paintAt = useCallback((cx: number, cy: number) => {
    const xToIdx = xToIndexRef.current;
    const yToVal = yToValueRef.current;
    const last = lastDragCoordsRef.current;
    lastDragCoordsRef.current = [cx, cy];

    const idx = xToIdx(cx);
    if (idx === null) return;

    const working = dragPatternRef.current;
    if (!working) return;

    let changed = false;

    if (last === null) {
      const val = yToVal(cy);
      if (working[idx] !== val) {
        working[idx] = val;
        changed = true;
      }
    } else {
      const lastIdx = xToIdx(last[0]);
      if (lastIdx === null) return;

      const startIdx = Math.min(lastIdx, idx);
      const endIdx = Math.max(lastIdx, idx);

      for (let i = startIdx; i <= endIdx; i++) {
        const t = lastIdx === idx ? 0 : (i - lastIdx) / (idx - lastIdx);
        const interpY = last[1] + t * (cy - last[1]);
        const val = yToVal(interpY);
        if (working[i] !== val) {
          working[i] = val;
          changed = true;
        }
      }
    }

    if (changed) onChangeRef.current([...working]);
  }, []);

  // Window-level drag handlers
  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const coords = getCanvasCoords(e);
      if (!coords) return;
      paintAt(coords[0], coords[1]);
    };

    const handleUp = () => {
      isDraggingRef.current = false;
      lastDragCoordsRef.current = null;
      dragPatternRef.current = null;
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [getCanvasCoords, paintAt]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (e.button !== 0) return;
    const coords = getCanvasCoords(e);
    if (!coords) return;
    isDraggingRef.current = true;
    lastDragCoordsRef.current = null;
    dragPatternRef.current = [...patternRef.current];
    paintAt(coords[0], coords[1]);
  }, [getCanvasCoords, paintAt]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setTextMode(true);
  }, []);

  const handleTextChange = useCallback((newValue: any[]) => {
    onChange(newValue);
    setTextMode(false);
  }, [onChange]);

  // Draw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Background
    ctx.fillStyle = trackColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    const len = pattern.length;
    if (len === 0) return;

    // Fill area under line
    const baseY = valueToY(Math.max(min, Math.min(max, 0)));
    ctx.fillStyle = hexToRgba(color, 0.15);
    ctx.beginPath();
    ctx.moveTo(indexToX(0), baseY);
    for (let i = 0; i < len; i++) {
      ctx.lineTo(indexToX(i), valueToY(pattern[i]));
    }
    ctx.lineTo(indexToX(len - 1), baseY);
    ctx.closePath();
    ctx.fill();

    // Line
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5 * PIXEL_RATIO;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    for (let i = 0; i < len; i++) {
      const x = indexToX(i);
      const y = valueToY(pattern[i]);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Dots
    ctx.fillStyle = color;
    for (let i = 0; i < len; i++) {
      const x = indexToX(i);
      const y = valueToY(pattern[i]);
      ctx.beginPath();
      ctx.arc(x, y, DOT_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [pattern, min, max, canvasWidth, canvasHeight, color, trackColor, indexToX, valueToY]);

  if (textMode) {
    return (
      <ArrayControl
        value={pattern}
        onChange={handleTextChange}
      />
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <canvas
        ref={canvasRef}
        width={canvasWidth}
        height={canvasHeight}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        style={{
          display: 'block',
          width: '100%',
          height: CANVAS_HEIGHT,
          cursor: 'crosshair',
          borderRadius: 3,
        }}
      />
    </div>
  );
}

function normalizeArray(value: any[], length: number, min: number): number[] {
  const arr = Array.isArray(value) ? value : [];
  if (arr.length === length) return arr;
  if (arr.length > length) return arr.slice(0, length);
  const padded = [...arr];
  while (padded.length < length) padded.push(min);
  return padded;
}

function hexToRgba(hex: string, alpha: number): string {
  const match = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!match) return `rgba(59, 130, 246, ${alpha})`;
  const r = parseInt(match[1], 16);
  const g = parseInt(match[2], 16);
  const b = parseInt(match[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
